import React, {useState, useEffect} from 'react';
import {getMassUploadFiles, deleteMassUploadFile, downloadMassUploadSourceFile, downloadMassCalculationResults, validateMassCalculationAvailability, cancelMassCalculation} from '../../services/massUploadApi';
import {toast} from 'react-toastify';
import MassUploadModal from '../../modals/massUploadModal';
import ConfirmModal from '../../components/Modals/ConfirmModal';
import GridHeader from '../../components/GridHeader';
import MassUploadFilters from './components/MassUploadFilters';
import PaginationControl from './components/PaginationControl';
import MassUploadTable from './components/MassUploadTable';
import {Button} from 'semantic-ui-react';
import {getRoles} from '../../services/userRights';

const MassUploadListPage = ({history}) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCalculationModal, setShowCalculationModal] = useState(false);
    const [calculationBalanceConfirmModal, setCalculationBalanceConfirmModal] = useState(null);
    const [hasError, setHasError] = useState(false);
    const roles = getRoles() || [];
    const canMassCalculation = roles.includes('Coordinator')
        || roles.includes('Admin')
        || roles.includes('GlobalAdmin');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [authorFilter, setAuthorFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50);
    const [previousStatuses, setPreviousStatuses] = useState({});
    const [previousDateNumbers, setPreviousDateNumbers] = useState({});
    const [previousErrorNumbers, setPreviousErrorNumbers] = useState({});

    useEffect(() => {
        loadFiles();
    }, []);

    useEffect(() => {
        // Автообновление каждые 5 секунд для отслеживания прогресса обработки
        // Но только если есть файлы в процессе обработки и нет ошибок
        if (hasError) return;

        const interval = setInterval(() => {
            const hasProcessingFiles = files.some(f => f.status === 'Processing');
            if (hasProcessingFiles) {
                loadFiles();
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [files, hasError]);

    useEffect(() => {
        // Проверяем изменение статуса с Processing на Completed
        files.forEach(file => {
            const prevStatus = previousStatuses[file.id];
            if (prevStatus === 'Processing' && file.status === 'Completed') {
                // Показываем тост без автозакрытия
                toast.info(
                    'Обработка списка массовой загрузки завершена. Ознакомиться с результатами и продолжить оформление можно в разделе "Массовые операции".',
                    {
                        autoClose: false,
                        closeButton: true,
                        closeOnClick: false
                    }
                );
            } else if (prevStatus === 'Processing' && file.status !== 'Processing') {
                // Показываем тост без автозакрытия
                toast.info(
                    'Массовый заказ отправлений завершен. Вы можете ознакомиться с результатами операции на вкладке Мои отправления или перейти по ссылке соответствующего файла на вкладке Массовые операции (пиктограмма списка в столбце "Действия").',
                    {
                        autoClose: false,
                        closeButton: true,
                        closeOnClick: false
                    }
                );
            }

            if (previousDateNumbers
                && previousDateNumbers[file.id] !== undefined
                && file.changedDepartureNumbers
                && file.changedDepartureNumbers.length > 0
                && (previousDateNumbers[file.id] || '') !== (file.changedDepartureNumbers || '')) {
                // Показываем тост без автозакрытия
                var parcelNumbers = (file.changedDepartureNumbers || '').replaceAll(',', ', ');
                toast.warning(
                    `По части заявок транспортные компании не предложили варианта сбора на указанную Вами дату. Дата сбора была автоматически перенесена на следующий рабочий день.`,
                    {
                        autoClose: false,
                        closeButton: true,
                        closeOnClick: false
                    }
                );
            }

            if (previousErrorNumbers
                && previousErrorNumbers[file.id] !== undefined
                && file.sendErrorsNumbers
                && file.sendErrorsNumbers.length > 0
                && (previousErrorNumbers[file.id] || '') !== (file.sendErrorsNumbers || '')) {
                // Показываем тост без автозакрытия
                var parcelNumbers = (file.sendErrorsNumbers || '').replaceAll(',', ', ');
                toast.error(
                    `По части заявок произошла ошибка при попытке отправки в транспортную компанию.`,
                    {
                        autoClose: false,
                        closeButton: true,
                        closeOnClick: false
                    }
                );
            }
        });
        
        // Обновляем предыдущие статусы
        const newStatuses = {};
        files.forEach(file => {
            newStatuses[file.id] = file.status;
        });
        setPreviousStatuses(newStatuses);
        
        // Обновляем предыдущие списки номеров с переносом даты отправки
        const dateNumbers = {};
        files.forEach(file => {
            dateNumbers[file.id] = file.changedDepartureNumbers;
        });
        setPreviousDateNumbers(dateNumbers);
        
        // Обновляем предыдущие списки номеров с ошибками отправки
        const errorNumbers = {};
        files.forEach(file => {
            errorNumbers[file.id] = file.sendErrorsNumbers;
        });
        setPreviousErrorNumbers(errorNumbers);
    }, [files]);

    const loadFiles = () => {
        getMassUploadFiles()
            .then(data => {
                const newFiles = data.files || [];
                // Убрана проверка изменения статусов здесь - она уже выполняется в useEffect
                setFiles(newFiles);
                setLoading(false);
                setHasError(false);
            })
            .catch(err => {
                console.error('Error loading files:', err);
                setFiles([]);
                setLoading(false);
                setHasError(true);
                
                // Показываем ошибку только один раз
                if (!hasError) {
                    toast.error('Ошибка при загрузке списка файлов. Возможно, миграция БД не выполнена.');
                }
            });
    };

    const handleUploadComplete = (result) => {
        setShowUploadModal(false);
        setShowCalculationModal(false);
        
        // Файл загружен, обновляем список
        loadFiles();
        
        // Можно сразу перейти на страницу файла (он будет в статусе Processing)
        // history.push(`/massupload/edit/${result.fileId}`);
    };

    const handleMassCalculationClick = () => {
        validateMassCalculationAvailability()
            .then(result => {
                if (result.error || result.warning) {
                    setCalculationBalanceConfirmModal({
                        open: true,
                        message: result.error || result.warning,
                        hasError: !!result.error
                    });
                    return;
                }
                setShowCalculationModal(true);
            })
            .catch(err => {
                console.error(err);
                toast.error('Не удалось проверить доступность массового расчета');
            });
    };

    const handleDeleteFile = (fileId) => {
        if (confirm('Вы уверены, что хотите удалить этот файл?')) {
            deleteMassUploadFile(fileId)
                .then(() => {
                    toast.success('Файл удален');
                    loadFiles();
                })
                .catch(err => {
                    console.error(err);
                    toast.error('Ошибка при удалении файла');
                });
        }
    };

    const handleCancelCalculation = (file) => {
        if (!file || !confirm('Остановить расчет для этого файла?')) {
            return;
        }

        cancelMassCalculation(file.id)
            .then(() => {
                toast.success('Расчет остановлен');
                loadFiles();
            })
            .catch(err => {
                console.error(err);
                let errorMessage = 'Ошибка при остановке расчета';
                if (err && typeof err === 'object') {
                    if (err.messages) {
                        const values = Object.values(err.messages).filter(Boolean);
                        if (values.length > 0) {
                            errorMessage = values.join('; ');
                        }
                    } else if (err.error) {
                        errorMessage = err.error;
                    } else if (err.message) {
                        errorMessage = err.message;
                    }
                }
                toast.error(errorMessage);
            });
    };

    const getFileClientCompanyIds = (file) =>
        (file.clientCompanyIds || []).map(id => id.toString());

    const fileMatchesCompany = (file, selectedCompanyId) => {
        if (!selectedCompanyId) {
            return true;
        }

        const clientCompanyIds = getFileClientCompanyIds(file);
        return clientCompanyIds.includes(selectedCompanyId.toString());
    };

    // Фильтрация по ЮЛ (без автора, чтобы получить список доступных авторов)
    const filesFilteredByCompany = files.filter(file => fileMatchesCompany(file, companyFilter));

    // Извлекаем уникальных авторов из файлов, отфильтрованных по ЮЛ
    // Если выбрано ЮЛ, показываем только авторов, у которых есть файлы с этим ЮЛ
    const authorsMap = new Map();
    filesFilteredByCompany.forEach(file => {
        if (file.authorId && file.authorName && !authorsMap.has(file.authorId)) {
            authorsMap.set(file.authorId, {
                id: file.authorId,
                name: file.authorName
            });
        }
    });
    const authors = Array.from(authorsMap.values()).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
    );

    const availableCompanyIds = Array.from(
        new Set(files.flatMap(file => getFileClientCompanyIds(file)))
    );

    // Сбрасываем фильтр по автору, если выбранный автор не входит в список доступных авторов для выбранного ЮЛ
    useEffect(() => {
        if (companyFilter && authorFilter) {
            const authorExists = authors.some(author => author.id.toString() === authorFilter);
            if (!authorExists) {
                setAuthorFilter('');
            }
        }
    }, [companyFilter, authors, authorFilter]);

    // Фильтрация и пагинация
    const filteredFiles = files.filter(file => {
        const matchesSearch = file.originalFileName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = !statusFilter || file.status === statusFilter;
        const matchesAuthor = !authorFilter || (file.authorId && file.authorId.toString() === authorFilter.toString());
        const matchesCompany = fileMatchesCompany(file, companyFilter);
        
        return matchesSearch && matchesStatus && matchesAuthor && matchesCompany;
    });

    const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedFiles = filteredFiles.slice(startIndex, endIndex);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleString('ru-RU');
    };

    if (loading) {
        return (
            <div className="page-content">
                <div className="loading-spinner">Загрузка...</div>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="page-content mass-upload-page">
                <div className="page-header">
                    <h1>Массовые операции</h1>
                </div>
                <div className="alert alert-danger">
                    <h4><i className="fa fa-exclamation-triangle"></i> Ошибка загрузки данных</h4>
                    <p>Не удалось загрузить список файлов. Возможные причины:</p>
                    <ul>
                        <li>Миграция базы данных не выполнена (таблица MassUploadFiles не создана)</li>
                        <li>Проблемы с подключением к базе данных</li>
                        <li>Ошибка на сервере</li>
                    </ul>
                    <button className="btn btn-primary" onClick={() => {
                        setHasError(false);
                        setLoading(true);
                        loadFiles();
                    }}>
                        <i className="fa fa-refresh"></i> Попробовать снова
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className='new-styles new-styles__p-0'>
            <div className='grid__header'>
                <GridHeader
                    name="Массовые операции"
                    btn={
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {canMassCalculation && (
                                <Button
                                    className='btn_light-blue'
                                    onClick={handleMassCalculationClick}
                                >
                                    Массовый расчет
                                </Button>
                            )}
                            <Button
                                className='btn_light-blue btn_icon_plus'
                                onClick={() => setShowUploadModal(true)}
                            >
                                Загрузить новый файл
                            </Button>
                        </div>
                    }
                />
                <MassUploadFilters
                    statusFilter={statusFilter}
                    authorFilter={authorFilter}
                    companyFilter={companyFilter}
                    searchTerm={searchTerm}
                    onStatusChange={setStatusFilter}
                    onAuthorChange={setAuthorFilter}
                    onCompanyChange={setCompanyFilter}
                    onSearchChange={setSearchTerm}
                    authors={authors}
                    availableCompanyIds={availableCompanyIds}
                />
            </div>

            <PaginationControl
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredFiles.length}
                startIndex={startIndex}
                endIndex={endIndex}
                onPageChange={setCurrentPage}
            />

            <MassUploadTable
                files={paginatedFiles}
                onEdit={(file) => {
                    if (file && file.fileType === 'Calculation') {
                        history.push(`/massupload/calculation/${file.id}`);
                    } else {
                        history.push(`/massupload/edit/${file.id}`);
                    }
                }}
                onView={(file) => {
                    // Переходим на "Мои отправления" с фильтром по ID файла массовой загрузки
                    if (!file.lastOperationDate) {
                        toast.warning('Накладные ещё не созданы для этого файла');
                        return;
                    }
                    
                    const filters = {
                        massUploadFileId: [file.id.toString()]
                    };
                    const filtersEncoded = encodeURIComponent(JSON.stringify(filters));
                    history.push(`/parcels/list/new?filters=${filtersEncoded}`);
                }}
                onDelete={handleDeleteFile}
                onCancel={handleCancelCalculation}
                onDownload={(file) => {
                    if (file && file.fileType === 'Calculation' && file.status === 'Calculated') {
                        downloadMassCalculationResults(file.id)
                            .catch(err => {
                                console.error(err);
                                toast.error('Ошибка при скачивании итогового файла');
                            });
                        return;
                    }

                    downloadMassUploadSourceFile(file.id)
                        .catch(err => {
                            console.error(err);
                            toast.error('Ошибка при скачивании исходного файла');
                        });
                }}
                formatDate={formatDate}
            />

            {showUploadModal && (
                <MassUploadModal
                    onClose={() => setShowUploadModal(false)}
                    onUploadComplete={handleUploadComplete}
                />
            )}
            {showCalculationModal && (
                <MassUploadModal
                    mode="calculation"
                    onClose={() => setShowCalculationModal(false)}
                    onUploadComplete={handleUploadComplete}
                />
            )}
            {calculationBalanceConfirmModal && (
                <ConfirmModal
                    open={calculationBalanceConfirmModal.open}
                    label="Массовый расчет"
                    setOpen={() => setCalculationBalanceConfirmModal(null)}
                    onConfirm={() => setShowCalculationModal(true)}
                    confirmBtnLabel="Продолжить"
                    closeBtnLabel="Отменить"
                    disabledBtn={calculationBalanceConfirmModal.hasError}
                >
                    <div>
                        <div style={{fontWeight: 700, fontSize: '1.15em', marginBottom: '0.75em'}}>
                            Внимание!
                        </div>
                        <div>{calculationBalanceConfirmModal.message}</div>
                    </div>
                </ConfirmModal>
            )}
        </div>
    );
};

export default MassUploadListPage;




// WEBPACK FOOTER //
// ./App/pages/massUpload/massUploadListPage.jsx